import {
	Avatar,
	Chip,
	Fab,
	List,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tooltip,
	useMediaQuery,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@emotion/react";
import { useSelector } from "react-redux";

import { formatToBase64ImageUrl, getInitials } from "../../utils/functions";
import { getAccessPermission } from "../../reducers/PermissionsReducer";
import PATEditButton from "../../components/Buttons/PATEditButton";
import { PATH_EMPLOYEE_EDIT } from "../../utils/routeConstants";
import PATViewButton from "../../components/Buttons/PATViewButton";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import EnhancedCard from "./EnhancedCard";
import { getService } from "../../reducers/service";

const useStyles = makeStyles((theme) => ({
	sectionBorder: {
		borderBottom: `1px solid ${theme.palette.colors.grey1}`,
	},
	table: {
		padding: "5px",
		paddingRight: "0px",
		paddingLeft: "10px",
		overflowX: "hidden",
		width: "100%",
	},
	employeeSelector: {
		"&:hover": {
			backgroundColor: "transparent",
		},
	},
	list: {
		marginBottom: "5px",
		maxHeight: "267px",
		borderTop: "3px solid ",
		borderRadius: "5px",
		overflow: "hidden",
		backgroundColor: theme.palette.background.paper,
	},
	listRoot: {
		paddingTop: 0,
		paddingBottom: 0,
		overflow: "scroll",
		"&::-webkit-scrollbar": {
			width: 10,
			height: 10,
		},
		"&::-webkit-scrollbar-thumb": {
			backgroundColor: "#888",
			visibility: "visible",
		},
	},
	scrollToTop: {
		position: "absolute !important",
		bottom: "50px !important",
		marginBottom: "30px !important",
		right: "35px !important",
		opacity: "0.5 !important",
		transition: "opacity 0.3s !important",
		width: "40px !important",
		height: "40px !important",
		"&:hover": {
			opacity: "1 !important",
		},
	},
	activeChip: {
		backgroundColor: "#1e8dfa !important",
		color:
			theme.palette.mode === "dark"
				? "#FFF !important"
				: "rgb(0, 0, 0) !important",
	},
	inactiveChip: {
		backgroundColor: "#d41e34 !important",
		color:
			theme.palette.mode === "dark"
				? "#FFF !important"
				: "rgb(0, 0, 0) !important",
	},
	empHeader: {
		whiteSpace: "nowrap !important",
		paddingTop: "0px !important",
		paddingBottom: "0px !important",
		color:
			theme.palette.mode === "dark"
				? "#FFF !important"
				: "rgb(0, 0, 0) !important",
	},
}));

const EmployeeCardView = (props) => {
	const {
		employees,
		isSelected,
		setSelected,
		setOpenDeleteDialog,
		handleClick,
	} = props;

	const classes = useStyles();
	const theme = useTheme();
	const history = useHistory();
	const smallAndDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
	const mdAndDown = useMediaQuery((theme) => theme.breakpoints.down("lg"));
	const employeePermission = getAccessPermission(
		"employees",
		"Employee Record",
		"any"
	);
	const userAgency = useSelector((state) => state.user.userAgency);

	const listHeight = smallAndDown ? 385 : mdAndDown ? 375 : 395;
	const canReadEmployees = !!employeePermission?.Read;
	const canEditEmployees = !!employeePermission?.Edit;
	const canDeleteEmployees = !!employeePermission?.Delete;

	const [scrollTopVisible, setScrollTopVisible] = useState(false);
	const [employeementStatusList, setEmployeementStatusList] = useState([]);

	const scrollRef = useRef(null);
	const visibleRef = useRef(false);

	// Constants for Employeement Status service
	const serviceName = "code-per-employment-status";
	const sortField = "code";
	const sortDirection = "asc";

	useEffect(() => {
		scrollRef.current.addEventListener("scroll", addOnScrollHandler);
		getEmployeementStatuses();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch Employeement Statuses
	const getEmployeementStatuses = async () => {
		const service = getService(serviceName);
		const query = {
			$limit: -1,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};
		const result = await service.find({ query });
		setEmployeementStatusList(result);
	};

	// Scroll to top of the list
	const scrollToTop = () => {
		scrollRef.current.scroll({ top: 0, behavior: "smooth" });
	};

	// Add scroll handler to show/hide scroll to top button
	const addOnScrollHandler = () => {
		if (
			!visibleRef.current &&
			scrollRef.current &&
			scrollRef.current.scrollTop > 10
		) {
			visibleRef.current = true;
			setScrollTopVisible(true);
		}
		if (
			visibleRef.current &&
			scrollRef.current &&
			scrollRef.current.scrollTop < 2
		) {
			visibleRef.current = false;
			setScrollTopVisible(false);
		}
	};

	// Scroll to top button component
	const scrollToTopButton = () => {
		return (
			<Tooltip title="Scroll to top" className={classes.scrollToTop}>
				<Fab onClick={scrollToTop} size="small" color="secondary">
					<KeyboardArrowUpIcon />
				</Fab>
			</Tooltip>
		);
	};

	// Get Employeement Status by code
	const getEmployeementStatus = (status) => {
		return employeementStatusList.find((option) => option.code === status);
	};

	// Enhanced Table Row
	const EnhancedTableRow = (index, row, isItemSelected, labelId) => {
		const selectedStatus = getEmployeementStatus(row.status);

		const rowStatus = selectedStatus ? selectedStatus.code : "";
		const rowStatusColor = row.status
			? selectedStatus?.activeEmployee
				? "#1e8dfa"
				: "#d41e34"
			: "#1e8dfa";

		let imageUrl = "";

		if (row.thumbnail?.imageData?.byteLength) {
			const base64 = formatToBase64ImageUrl(row.thumbnail.imageData);
			if (base64) imageUrl = base64;
		}

		return (
			<div
				className={classes.list}
				key={index}
				style={{
					borderTopColor: rowStatusColor,
				}}
			>
				<div className={classes.sectionBorder}>
					<TableRow
						onMouseEnter={(event) => handleClick(event, row.id)}
						onMouseLeave={() => setSelected(null)}
						aria-checked={isItemSelected}
						tabIndex={-1}
						label={labelId}
						key={row.id}
						className={classes.employeeSelector}
						selected={isItemSelected}
					>
						<TableCell
							align="left"
							style={{
								width: "2%",
							}}
						>
							{imageUrl ? (
								<img
									src={imageUrl}
									alt="Thumbnail"
									style={{
										width: 36,
										height: 36,
										borderRadius: "50%",
										objectFit: "cover",
									}}
								/>
							) : (
								<Avatar
									sx={{
										width: 36,
										height: 36,
										fontSize: 14,
										bgcolor: "#90caf9",
									}}
								>
									{getInitials(row?.firstName, row?.lastName)}
								</Avatar>
							)}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{row?.employeeNum || "-"}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{row?.firstName || "-"}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{row?.middleName || "-"}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{row?.lastName || "-"}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{row["rankInfo.description"] || "-"}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{row["titlePositionInfo.description"] || "-"}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{row["sectionInfo.description"] || "-"}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{row["departmentInfo.description"] || "-"}
						</TableCell>
						<TableCell
							align="left"
							className={classes.empHeader}
							style={{
								width: "10%",
							}}
						>
							{rowStatus ? (
								<Chip
									key={row.id}
									className={
										getEmployeementStatus(rowStatus)
											?.activeEmployee
											? classes.activeChip
											: classes.inactiveChip
									}
									label={rowStatus}
									size="large"
									variant="default"
								/>
							) : (
								""
							)}
						</TableCell>
						<TableCell
							align="right"
							style={{
								width: "1%",
								paddingTop: "0px",
								paddingBottom: "0px",
							}}
						>
							<Table>
								<TableBody>
									<TableRow>
										<TableCell
											align="right"
											sx={{
												padding: 0.5,
												borderBottom: 0,
											}}
										>
											{canEditEmployees && (
												<PATEditButton
													tooltipText="Edit Employee"
													onClick={() =>
														history.push(
															`${PATH_EMPLOYEE_EDIT}${row.id}`
														)
													}
													size="20px"
												/>
											)}
										</TableCell>
										<TableCell
											align="right"
											sx={{
												padding: 0.5,
												borderBottom: 0,
											}}
										>
											{canReadEmployees &&
												!canEditEmployees && (
													<PATViewButton
														tooltipText="View Employee Details"
														onClick={() =>
															history.push({
																pathname: `${PATH_EMPLOYEE_EDIT}${row.id}`,
																search: "?readOnly=true",
															})
														}
														size="20px"
													/>
												)}
										</TableCell>
										<TableCell
											align="right"
											sx={{
												padding: 0.5,
												borderBottom: 0,
											}}
										>
											{canDeleteEmployees && (
												<PATDeleteButton
													tooltipText="Delete Employee"
													onClick={() => {
														setSelected(row.id);
														setOpenDeleteDialog(
															true
														);
													}}
													size="20px"
												/>
											)}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableCell>
					</TableRow>
				</div>
				<div>
					<EnhancedCard row={row} />
				</div>
			</div>
		);
	};

	return (
		<div>
			<List
				className={classes.listRoot}
				style={{
					height: `calc(100vh - ${listHeight}px)`,
					padding: employees?.length > 0 ? "8px 0px" : "0px",
					marginTop: "5px",
				}}
				ref={scrollRef}
			>
				{employees?.length > 0 ? (
					<Table
						stickyHeader
						className={classes.table}
						aria-label="sticky table"
						size="small"
						style={{ width: "100%", height: "100%" }}
					>
						<TableBody>
							{employees.map((row, index) => {
								const isItemSelected = isSelected(row.id);
								const labelId = `enhanced-table-checkbox-${index}`;

								return EnhancedTableRow(
									index,
									row,
									isItemSelected,
									labelId
								);
							})}
						</TableBody>
					</Table>
				) : (
					<div
						style={{
							borderLeft: `1px solid ${theme.palette.divider}`,
							borderRight: `1px solid ${theme.palette.divider}`,
							borderTop: `1px solid ${theme.palette.divider}`,
							height: `calc(100vh - ${listHeight}px)`,
							position: "relative",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							fontSize: "18px",
							color: theme.palette.text.secondary,
						}}
					>
						No Records Found
					</div>
				)}
			</List>
			{scrollTopVisible && scrollToTopButton()}
		</div>
	);
};

export default EmployeeCardView;
